/**
 * Formulario para conectar fibras entre cables (empalme de continuidad/fusión/mecánico)
 * Crea el empalme vía la API real y marca ambas fibras como fusionadas
 * @module components/forms/FiberConnectionForm
 */

import { useState } from 'react'
import { Link2, ArrowRight, Circle, AlertTriangle } from 'lucide-react'
import { useFiberStore } from '@/stores'
import { connectionServiceApi } from '@/services/connectionServiceApi'
import { fusionarFibrasPorExtremo } from '@/services/connectionService'
import { cableTouchesNode, cableEndpointAtNode } from '@/utils/cableEndpoints'
import type { Cable } from '@/types/fibers'

/** Estado real de una fibra vista desde el extremo que toca `nodeId` (no el agregado deprecado). */
function estadoEnExtremo(fibra: { estado: string; extremos?: { origen: { estado: string }; destino: { estado: string } } }, extremo: 'origen' | 'destino' | null): string {
  if (extremo && fibra.extremos) return fibra.extremos[extremo].estado
  return fibra.estado
}

/** Busca el fibraId real de una fibra dentro de un cable (por buffer+número, o número suelto). */
function fibraIdEnCable(cable: Cable, bufferNum: number | null | undefined, fibraNum: number): string | null {
  if (cable.tieneBuffers && cable.buffers) {
    const buffer = cable.buffers.find((b) => b.numero === bufferNum)
    return buffer?.fibras.find((f) => f.numero === fibraNum)?.fibraId ?? null
  }
  return cable.fibras?.find((f) => f.numero === fibraNum)?.fibraId ?? null
}

const COLORES_FIBRA = [
  { nombre: 'Azul', codigo: 'azul', hex: '#0066CC' },
  { nombre: 'Naranja', codigo: 'naranja', hex: '#FF6600' },
  { nombre: 'Verde', codigo: 'verde', hex: '#00AA00' },
  { nombre: 'Marrón', codigo: 'marron', hex: '#8B4513' },
  { nombre: 'Gris', codigo: 'gris', hex: '#808080' },
  { nombre: 'Blanco', codigo: 'blanco', hex: '#FFFFFF' },
  { nombre: 'Rojo', codigo: 'rojo', hex: '#FF0000' },
  { nombre: 'Negro', codigo: 'negro', hex: '#000000' },
  { nombre: 'Amarillo', codigo: 'amarillo', hex: '#FFCC00' },
  { nombre: 'Violeta', codigo: 'violeta', hex: '#9900CC' },
  { nombre: 'Rosa', codigo: 'rosa', hex: '#FF66CC' },
  { nombre: 'Turquesa', codigo: 'turquesa', hex: '#00CCCC' },
]

interface NuevaConexion {
  tipoEmpalme: 'fusion' | 'mecanico'
  bandeja: number
  posicion: number
  cableOrigenId?: string
  bufferOrigen?: number | null
  fibraOrigen?: number
  colorOrigen?: string
  cableDestinoId?: string
  bufferDestino?: number | null
  fibraDestino?: number
  colorDestino?: string
}

interface CableInfo {
  id: string
  nombre: string
  tipo: string
  numeroFibras: number
  tieneBuffers: boolean
  buffers?: Array<{
    numero: number
    color: string
    fibras: Array<{
      numero: number
      color: string
      estado: string
    }>
  }>
  fibras?: Array<{
    numero: number
    color: string
    estado: string
  }>
}

interface FiberConnectionFormProps {
  puntoEmpalmeId: string
  puntoEmpalmeNombre: string
  onCreated: () => void
  onCancel: () => void
}

export function FiberConnectionForm({
  puntoEmpalmeId,
  puntoEmpalmeNombre,
  onCreated,
  onCancel,
}: FiberConnectionFormProps) {
  const cables = useFiberStore((state) => state.cables)
  const updateCable = useFiberStore((state) => state.updateCable)

  const [conexion, setConexion] = useState<NuevaConexion>({
    tipoEmpalme: 'fusion',
    bandeja: 1,
    posicion: 1,
  })
  const [saving, setSaving] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const cablesConectados: CableInfo[] = cables
    .filter((c) => cableTouchesNode(c, puntoEmpalmeId))
    .map((c) => {
      // Cada cable puede tocar este nodo por su lado origen o destino -- el
      // estado de sus fibras que importa aquí es el de ESE extremo, no el
      // agregado de todo el cable.
      const extremo = cableEndpointAtNode(c, puntoEmpalmeId)
      return {
        id: c.id,
        nombre: c.nombre || `Cable ${c.id.slice(-6)}`,
        tipo: c.tipo,
        numeroFibras: c.numeroFibras,
        tieneBuffers: c.tieneBuffers,
        buffers: c.buffers?.map((b) => ({
          numero: b.numero,
          color: b.color,
          fibras: b.fibras.map((f) => ({
            numero: f.numero,
            color: f.color,
            estado: estadoEnExtremo(f, extremo),
          })),
        })),
        fibras: c.fibras?.map((f) => ({
          numero: f.numero,
          color: f.color,
          estado: estadoEnExtremo(f, extremo),
        })),
      }
    })

  const getColorHex = (colorCodigo: string) => {
    return COLORES_FIBRA.find((c) => c.codigo === colorCodigo)?.hex || '#CCCCCC'
  }

  const guardarConexion = async () => {
    setValidationError(null)

    if (!conexion.cableOrigenId || conexion.fibraOrigen === undefined) {
      setValidationError('Debe seleccionar cable y fibra de origen')
      return
    }

    if (!conexion.cableDestinoId || conexion.fibraDestino === undefined) {
      setValidationError('Debe seleccionar cable y fibra de destino')
      return
    }

    if (
      conexion.cableOrigenId === conexion.cableDestinoId &&
      (conexion.bufferOrigen ?? null) === (conexion.bufferDestino ?? null) &&
      conexion.fibraOrigen === conexion.fibraDestino
    ) {
      setValidationError('No se puede conectar una fibra consigo misma')
      return
    }

    const cableOrigenActual = cables.find((c) => c.id === conexion.cableOrigenId)
    const cableDestinoActual = cables.find((c) => c.id === conexion.cableDestinoId)

    if (!cableOrigenActual || !cableDestinoActual) {
      setValidationError('No se encontró alguno de los cables seleccionados')
      return
    }

    // Un cable tiene dos puntas independientes -- hay que empalmar la que
    // realmente toca este nodo, no la fibra entera de punta a punta.
    const extremoOrigen = cableEndpointAtNode(cableOrigenActual, puntoEmpalmeId)
    const extremoDestino = cableEndpointAtNode(cableDestinoActual, puntoEmpalmeId)
    if (!extremoOrigen || !extremoDestino) {
      setValidationError('No se pudo determinar el extremo de uno de los cables en este nodo')
      return
    }

    setSaving(true)
    try {
      const atenuacion = conexion.tipoEmpalme === 'fusion' ? 0.02 : 0.1

      await connectionServiceApi.create({
        tipo: conexion.tipoEmpalme === 'fusion' ? 'FUSION' : 'MECANICO',
        cableEntradaId: conexion.cableOrigenId,
        fibraEntrada: conexion.fibraOrigen,
        bufferEntrada: conexion.bufferOrigen ?? undefined,
        cableSalidaId: conexion.cableDestinoId,
        fibraSalida: conexion.fibraDestino,
        bufferSalida: conexion.bufferDestino ?? undefined,
        nodeId: puntoEmpalmeId,
        bandeja: conexion.bandeja,
        posicion: conexion.posicion,
        atenuacion,
      })

      // Marcar fusionado solo el extremo de cada fibra que realmente se tocó
      // aquí -- el otro extremo de cada cable sigue disponible para lo suyo.
      const fibraIdOrigen =
        fibraIdEnCable(cableOrigenActual, conexion.bufferOrigen, conexion.fibraOrigen) ||
        `${cableOrigenActual.id}|${conexion.bufferOrigen ?? 'SB'}|${conexion.colorOrigen}`
      const fibraIdDestino =
        fibraIdEnCable(cableDestinoActual, conexion.bufferDestino, conexion.fibraDestino) ||
        `${cableDestinoActual.id}|${conexion.bufferDestino ?? 'SB'}|${conexion.colorDestino}`

      const { cableOrigen: origenActualizado, cableDestino: destinoActualizado } = fusionarFibrasPorExtremo({
        cableOrigen: cableOrigenActual as any,
        extremoOrigen,
        bufferOrigenNum: conexion.bufferOrigen ?? null,
        fibraOrigenNum: conexion.fibraOrigen,
        fibraIdOrigen,
        cableDestino: cableDestinoActual as any,
        extremoDestino,
        bufferDestinoNum: conexion.bufferDestino ?? null,
        fibraDestinoNum: conexion.fibraDestino,
        fibraIdDestino,
      })

      await updateCable(origenActualizado as Cable)
      if (cableOrigenActual.id !== cableDestinoActual.id) {
        await updateCable(destinoActualizado as Cable)
      }

      onCreated()
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : 'Error al guardar la conexión')
    } finally {
      setSaving(false)
    }
  }

  const seleccionarFibra = (
    lado: 'origen' | 'destino',
    cableId: string,
    bufferNum: number | null,
    fibraNum: number,
    fibraColor: string
  ) => {
    if (lado === 'origen') {
      setConexion((prev) => ({
        ...prev,
        cableOrigenId: cableId,
        bufferOrigen: bufferNum,
        fibraOrigen: fibraNum,
        colorOrigen: fibraColor,
      }))
    } else {
      setConexion((prev) => ({
        ...prev,
        cableDestinoId: cableId,
        bufferDestino: bufferNum,
        fibraDestino: fibraNum,
        colorDestino: fibraColor,
      }))
    }
  }

  const getCableName = (cableId: string) => {
    const cable = cablesConectados.find((c) => c.id === cableId)
    return cable?.nombre || cableId
  }

  return (
    <div className="border-2 border-blue-500 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/10 space-y-4">
      <h5 className="font-medium flex items-center gap-2">
        <Link2 className="w-4 h-4" />
        Nueva Conexión de Fibra en {puntoEmpalmeNombre}
      </h5>

      {validationError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-3">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">{validationError}</span>
          </div>
        </div>
      )}

      {cablesConectados.length === 0 && (
        <p className="text-sm text-gray-500">No hay cables conectados a este punto</p>
      )}

      {/* Tipo de empalme y ubicación */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Tipo de Empalme</label>
          <select
            value={conexion.tipoEmpalme}
            onChange={(e) =>
              setConexion((prev) => ({ ...prev, tipoEmpalme: e.target.value as 'fusion' | 'mecanico' }))
            }
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            <option value="fusion">🔥 Fusión (~0.02 dB)</option>
            <option value="mecanico">🔧 Mecánico (~0.1 dB)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Bandeja</label>
          <input
            type="number"
            value={conexion.bandeja}
            onChange={(e) => setConexion((prev) => ({ ...prev, bandeja: parseInt(e.target.value) || 1 }))}
            min={1}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Posición</label>
          <input
            type="number"
            value={conexion.posicion}
            onChange={(e) => setConexion((prev) => ({ ...prev, posicion: parseInt(e.target.value) || 1 }))}
            min={1}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
        </div>
      </div>

      {/* Selectores de fibra */}
      <div className="grid grid-cols-2 gap-4">
        {/* Fibra Origen */}
        <div className="border rounded-lg p-3 dark:border-dark-600">
          <label className="block text-sm font-medium mb-2">Fibra Origen</label>
          {conexion.cableOrigenId ? (
            <div className="flex items-center gap-2 p-2 bg-white dark:bg-dark-700 rounded mb-2">
              <Circle
                className="w-4 h-4"
                fill={getColorHex(conexion.colorOrigen || 'azul')}
                stroke={getColorHex(conexion.colorOrigen || 'azul')}
              />
              <span className="text-sm flex-1">
                {getCableName(conexion.cableOrigenId)} →{conexion.bufferOrigen && ` B${conexion.bufferOrigen}/`}
                F{conexion.fibraOrigen}
              </span>
              <button
                type="button"
                onClick={() =>
                  setConexion((prev) => ({
                    ...prev,
                    cableOrigenId: undefined,
                    bufferOrigen: undefined,
                    fibraOrigen: undefined,
                    colorOrigen: undefined,
                  }))
                }
                className="text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          ) : (
            <FiberSelector
              cables={cablesConectados}
              onSelect={(cableId, buffer, fibra, color) => seleccionarFibra('origen', cableId, buffer, fibra, color)}
            />
          )}
        </div>

        {/* Fibra Destino */}
        <div className="border rounded-lg p-3 dark:border-dark-600">
          <label className="block text-sm font-medium mb-2">Fibra Destino</label>
          {conexion.cableDestinoId ? (
            <div className="flex items-center gap-2 p-2 bg-white dark:bg-dark-700 rounded mb-2">
              <Circle
                className="w-4 h-4"
                fill={getColorHex(conexion.colorDestino || 'azul')}
                stroke={getColorHex(conexion.colorDestino || 'azul')}
              />
              <span className="text-sm flex-1">
                {getCableName(conexion.cableDestinoId)} →
                {conexion.bufferDestino && ` B${conexion.bufferDestino}/`}
                F{conexion.fibraDestino}
              </span>
              <button
                type="button"
                onClick={() =>
                  setConexion((prev) => ({
                    ...prev,
                    cableDestinoId: undefined,
                    bufferDestino: undefined,
                    fibraDestino: undefined,
                    colorDestino: undefined,
                  }))
                }
                className="text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          ) : (
            <FiberSelector
              cables={cablesConectados}
              onSelect={(cableId, buffer, fibra, color) => seleccionarFibra('destino', cableId, buffer, fibra, color)}
            />
          )}
        </div>
      </div>

      {/* Preview de conexión */}
      {conexion.cableOrigenId && conexion.cableDestinoId && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-300 dark:border-green-700">
          <div className="flex items-center justify-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Circle
                className="w-3 h-3"
                fill={getColorHex(conexion.colorOrigen || 'azul')}
                stroke={getColorHex(conexion.colorOrigen || 'azul')}
              />
              <span>
                {getCableName(conexion.cableOrigenId)}
                {conexion.bufferOrigen && `/B${conexion.bufferOrigen}`}/F{conexion.fibraOrigen}
              </span>
            </div>
            <ArrowRight className="w-5 h-5 text-green-600" />
            <div className="flex items-center gap-2">
              <Circle
                className="w-3 h-3"
                fill={getColorHex(conexion.colorDestino || 'azul')}
                stroke={getColorHex(conexion.colorDestino || 'azul')}
              />
              <span>
                {getCableName(conexion.cableDestinoId)}
                {conexion.bufferDestino && `/B${conexion.bufferDestino}`}/F{conexion.fibraDestino}
              </span>
            </div>
            <span className="text-orange-600 font-medium">
              {(conexion.tipoEmpalme === 'fusion' ? 0.02 : 0.1).toFixed(2)} dB
            </span>
          </div>
        </div>
      )}

      {/* Botones */}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={guardarConexion}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Guardando...' : 'Guardar Conexión'}
        </button>
      </div>
    </div>
  )
}

function FiberSelector({
  cables,
  onSelect,
}: {
  cables: CableInfo[]
  onSelect: (cableId: string, buffer: number | null, fibra: number, color: string) => void
}) {
  const [selectedCable, setSelectedCable] = useState<string>('')
  const [selectedBuffer, setSelectedBuffer] = useState<number | null>(null)

  const cable = cables.find((c) => c.id === selectedCable)
  const tieneBuffers = cable?.tieneBuffers && cable.buffers && cable.buffers.length > 0

  const getColorHex = (colorCodigo: string) => {
    return COLORES_FIBRA.find((c) => c.codigo === colorCodigo)?.hex || '#CCCCCC'
  }

  return (
    <div className="space-y-2">
      <select
        value={selectedCable}
        onChange={(e) => {
          setSelectedCable(e.target.value)
          setSelectedBuffer(null)
        }}
        className="w-full px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
      >
        <option value="">Seleccionar cable...</option>
        {cables.map((c) => (
          <option key={c.id} value={c.id}>
            {c.nombre} ({c.tipo} - {c.numeroFibras} FO)
          </option>
        ))}
      </select>

      {cable && tieneBuffers && (
        <select
          value={selectedBuffer || ''}
          onChange={(e) => setSelectedBuffer(e.target.value ? parseInt(e.target.value) : null)}
          className="w-full px-2 py-1 text-sm border rounded dark:bg-dark-700 dark:border-dark-600"
        >
          <option value="">Seleccionar buffer...</option>
          {cable.buffers?.map((b) => (
            <option key={b.numero} value={b.numero}>
              Buffer {b.numero} - {b.color.charAt(0).toUpperCase() + b.color.slice(1)}
            </option>
          ))}
        </select>
      )}

      {cable && (!tieneBuffers || selectedBuffer !== null) && (
        <div className="grid grid-cols-6 gap-1 max-h-32 overflow-y-auto">
          {(tieneBuffers && selectedBuffer !== null
            ? cable.buffers?.find((b) => b.numero === selectedBuffer)?.fibras
            : cable.fibras
          )?.map((fibra) => {
            const libre = fibra.estado === 'libre'
            return (
              <button
                key={fibra.numero}
                type="button"
                onClick={() => onSelect(selectedCable, selectedBuffer, fibra.numero, fibra.color)}
                disabled={!libre}
                className={`p-1 border rounded text-xs ${
                  !libre
                    ? 'opacity-40 cursor-not-allowed bg-gray-100 dark:bg-dark-600'
                    : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
                }`}
                style={{ borderColor: getColorHex(fibra.color) }}
                title={`F${fibra.numero} - ${fibra.color} (${fibra.estado})`}
              >
                <Circle className="w-2 h-2 mx-auto" fill={getColorHex(fibra.color)} stroke={getColorHex(fibra.color)} />
                <div className="font-medium">F{fibra.numero}</div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
